import { useCallback } from 'react';
import { useAppStorageFiles } from '@workspace/ui/hooks/useAppStorage';
import { useAuth } from '@workspace/ui/hooks/useAuth';
import type { MessageAttachment, UploadedFile } from '../types';
// Single definition, so the delete prefix and the export prefix cannot drift
// apart — they must describe the same set of objects or one of them is wrong.
import { sessionStoragePrefix } from '../export/conversation-export';
import { recordEvent } from '@workspace/observability/browser';

/** App scope for chat artifacts — matches the one sessions already use. */
const APP_NAME = 'chat';

/**
 * Artifact kinds, which are also the folder segment of a key.
 *
 * `<sessionId>/<kind>/<uuid>.<ext>` means one `deletePrefix('<sessionId>/')`
 * removes everything a conversation produced, and one prefix list enumerates it
 * for export (tdd-0076).
 */
export type ArtifactKind = 'uploads' | 'masks' | 'generated';

export interface PersistArtifactInput {
  sessionId: string;
  kind: ArtifactKind;
  /** Raw bytes. */
  blob: Blob;
  /** Original filename, preserved in metadata and restored on export. */
  displayName: string;
  contentType: string;
  messageId?: string;
  /** Lineage, when this artifact came from editing another. */
  derivedFrom?: string;
  rootKey?: string;
  editPrompt?: string;
  /**
   * Report upload progress, for an attachment the user is watching.
   *
   * Passing this switches the storage client to XHR, which is the only
   * transport that can observe request-body progress. Omit it for background
   * writes, where a bar has nobody looking at it.
   */
  onProgress?: (percent: number) => void;
  /**
   * Write to THIS key rather than minting a new one.
   *
   * Set when the key was already assigned and sent to the model, so the bytes
   * land where the model was told to look.
   */
  storageKey?: string;
}

/**
 * Derive a safe file extension from the display name.
 *
 * The extension is the ONLY part of a user-supplied filename that reaches the
 * key, and it is filtered to a conservative alphanumeric shape. The name itself
 * rides in metadata: filenames are neither unique within a conversation (two
 * `screenshot.png` uploads must not collide) nor safe to interpolate into a
 * storage path.
 */
function safeExtension(displayName: string, contentType: string): string {
  const fromName = displayName.match(/\.([A-Za-z0-9]{1,8})$/)?.[1];
  if (fromName) return fromName.toLowerCase();

  // Fall back to the content type's subtype, so a name without an extension
  // still produces something recognisable rather than a bare uuid.
  const subtype = contentType.split('/')[1]?.split(';')[0];
  if (subtype && /^[A-Za-z0-9]{1,8}$/.test(subtype)) return subtype.toLowerCase();

  return 'bin';
}

function newId(): string {
  // crypto.randomUUID is unavailable on non-secure origins; the fallback only
  // needs to be collision-resistant within one conversation's prefix.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildArtifactKey(
  sessionId: string,
  kind: ArtifactKind,
  displayName: string,
  contentType: string,
): string {
  return `${sessionId}/${kind}/${newId()}.${safeExtension(displayName, contentType)}`;
}

/**
 * Persist chat artifacts to the user's storage scope (tdd-0076, Phase 1).
 *
 * Two rules govern every call here:
 *
 * PRIVATE SESSIONS ARE NEVER WRITTEN. `isPrivate` threads live only in
 * sessionStorage and never persist remotely; writing their attachments to
 * durable storage would silently break that guarantee. This is an explicit
 * guard, not an emergent property, and it fails closed.
 *
 * PERSISTENCE IS BEST-EFFORT. A storage failure must never fail the user's
 * message — the attachment still travels inline via its data URL, exactly as it
 * did before this existed. The cost of failure is that the artifact is not
 * addressable by a tool, not that the turn breaks.
 */
/** The one storage operation these functions need — an injectable seam. */
export type UploadFn = (
  key: string,
  blob: Blob,
  options: {
    contentType: string;
    metadata: Record<string, string>;
    /** 0-100 as the bytes go up. Omitted when nothing is showing a bar. */
    onProgress?: (percent: number) => void;
  },
) => Promise<unknown>;

/** What a conversation's artifacts amount to, for the delete confirmation. */
export interface ArtifactSummary {
  count: number;
  bytes: number;
}

/** Outcome of removing a conversation's artifacts. */
export interface PrefixDeleteResult {
  deleted: number;
  failed: number;
}

/**
 * The storage prefix owning every artifact a conversation produced.
 *
 * Uploads, masks and generations all live beneath it, which is what makes
 * "delete this conversation's artifacts" a single prefix operation rather than
 * a scan (tdd-0076).
 */
export const sessionPrefix = sessionStoragePrefix;

/**
 * Guard against a prefix that would reach further than one conversation.
 *
 * `deletePrefix` refuses empty and `/` prefixes at the storage layer, but a bug
 * that computed a *plausible-looking* prefix would sail past that. A malformed
 * session id must never become "delete this user's entire chat scope".
 */
export function isSafeSessionPrefix(prefix: string): boolean {
  return /^session-\d+\/$/.test(prefix);
}

/** Metadata written alongside an artifact's bytes. */
export function buildArtifactMetadata(input: PersistArtifactInput): Record<string, string> {
  const metadata: Record<string, string> = {
    sessionId: input.sessionId,
    displayName: input.displayName,
    source:
      input.kind === 'uploads' ? 'upload' : input.kind === 'masks' ? 'mask' : 'generation',
  };
  if (input.messageId) metadata.messageId = input.messageId;
  if (input.derivedFrom) metadata.derivedFrom = input.derivedFrom;
  if (input.rootKey) metadata.rootKey = input.rootKey;
  // Bounded: metadata rides in a header, and a long prompt would blow the
  // header size limit for a field that is only ever informational.
  if (input.editPrompt) metadata.editPrompt = input.editPrompt.slice(0, 512);
  return metadata;
}

/**
 * Write one artifact, returning its key — or null if it was not persisted.
 *
 * Null is a normal outcome, not an error: the caller degrades to the inline
 * data URL, which is exactly what happened before persistence existed.
 */
export async function persistArtifactWith(
  upload: UploadFn,
  input: PersistArtifactInput,
): Promise<string | null> {
  // Reuse a key that was already handed to the model, when there is one —
  // minting a second key here would store the bytes somewhere the model has
  // no reference to, which is worse than not storing them at all.
  const key =
    input.storageKey ??
    buildArtifactKey(
      input.sessionId,
      input.kind,
      input.displayName,
      input.contentType,
    );

  try {
    await upload(key, input.blob, {
      contentType: input.contentType,
      metadata: buildArtifactMetadata(input),
      ...(input.onProgress ? { onProgress: input.onProgress } : {}),
    });
    return key;
  } catch (error) {
    console.error('[artifact-store] persist failed, continuing inline:', key, error);
    // Degrading to inline is correct — the turn still works — but it silently
    // costs the model an editing handle, so it must not also be invisible.
    // `kind` and not the key: span attributes, so the key would be a new series
    // per artifact.
    recordEvent('chat.artifact.persist_failed', { kind: input.kind }, { error: true });
    return null;
  }
}

export interface PersistAttachmentsOptions {
  sessionId: string;
  isPrivate: boolean;
  authenticated: boolean;
  messageId?: string;
}

/**
 * Assign storage keys WITHOUT uploading (tdd-0076 Phase 2).
 *
 * The key is minted client-side and storage writes to whatever path we name —
 * it never generates or returns an id — so the key is knowable before the bytes
 * are anywhere. That matters because the model needs the key IN the request it
 * is answering: persistence is fired after send (so a storage round-trip never
 * delays the message appearing), which would otherwise mean an image could not
 * be edited until the user's next turn.
 *
 * Minting first and uploading after collapses that gap without reintroducing
 * the latency, and without a new storage endpoint.
 *
 * The trade: a key reaches the model slightly before the bytes reach storage.
 * If the upload then fails, the model holds a key that resolves to nothing —
 * a tool-call error rather than a wrong answer, and `edit_image` must say
 * "not found, it may still be uploading" rather than something misleading.
 *
 * Returns attachments unchanged for a private or unauthenticated session, which
 * is what keeps those out of storage entirely.
 */
export function assignArtifactKeys(
  attachments: MessageAttachment[],
  opts: Pick<PersistAttachmentsOptions, 'sessionId' | 'isPrivate' | 'authenticated'>,
): MessageAttachment[] {
  if (opts.isPrivate || !opts.authenticated) {
    // Say which gate closed, and only when it actually costs something.
    //
    // Both branches are correct behaviour, but they are indistinguishable from
    // a bug at the point the user notices: the attachment sends, the model
    // answers, and nothing anywhere says the editing handle was never minted.
    // Silence here already produced one wrong diagnosis.
    if (attachments.length > 0) {
      // Which gate, as an attribute — this is the answer to "why can the model
      // not edit my image" and both values are legitimate states, so it is
      // recorded without being marked an error.
      recordEvent('chat.attachments.unkeyed', {
        count: attachments.length,
        gate: opts.isPrivate ? 'private' : 'unauthenticated',
      });
      console.info(
        `[artifact-store] no storage key for ${attachments.length} attachment(s): ` +
          (opts.isPrivate
            ? 'private session (never persisted, by design)'
            : 'not authenticated'),
      );
    }
    return attachments;
  }

  return attachments.map((attachment) => ({
    ...attachment,
    storageKey: buildArtifactKey(
      opts.sessionId,
      'uploads',
      attachment.name,
      attachment.type,
    ),
  }));
}

/**
 * Persist a message's attachments and return them carrying their storage keys.
 *
 * Called on SEND rather than on attach, because a session id does not exist at
 * attach time — sessions are created lazily on the first message. Persisting
 * here means one write, always correctly keyed, and a user who attaches a file
 * then abandons the conversation leaves nothing behind.
 *
 * Attachments that could not be persisted are returned UNCHANGED rather than
 * dropped or flagged, so a storage outage is invisible to the conversation.
 */
export async function persistAttachmentsWith(
  upload: UploadFn,
  files: UploadedFile[],
  attachments: MessageAttachment[],
  opts: PersistAttachmentsOptions,
): Promise<MessageAttachment[]> {
  // The private guard. Fails closed and is checked before anything else —
  // a private session's bytes must never reach durable storage.
  if (opts.isPrivate) return attachments;
  if (!opts.authenticated || files.length === 0) return attachments;

  const byId = new Map(files.map((f) => [f.id, f]));

  return Promise.all(
    attachments.map(async (attachment) => {
      const file = byId.get(attachment.id);
      if (!file) return attachment;

      const blob = await dataUrlToBlob(file.dataUrl);
      if (!blob) return attachment;

      const key = await persistArtifactWith(upload, {
        sessionId: opts.sessionId,
        kind: 'uploads',
        blob,
        displayName: file.name,
        contentType: file.type || blob.type || 'application/octet-stream',
        messageId: opts.messageId,
        // Honour a key already assigned and sent to the model.
        storageKey: attachment.storageKey,
      });

      // On failure, DROP a pre-assigned key: leaving it would tell the UI the
      // file is stored when the bytes never arrived, and the model would hold
      // a reference that resolves to nothing with no signal anything is wrong.
      if (!key) {
        return attachment.storageKey
          ? { ...attachment, storageKey: undefined }
          : attachment;
      }

      return { ...attachment, storageKey: key };
    }),
  );
}

export function useArtifactStore() {
  const { upload, remove, listPrefix, removePrefix } = useAppStorageFiles(APP_NAME);
  const { authenticated } = useAuth();

  /**
   * Delete one stored artifact.
   *
   * Returns whether it went. The caller shows the outcome rather than assuming
   * success — a delete that silently failed would leave the user believing bytes
   * were removed when they were not, which is the one lie this feature cannot
   * afford.
   */
  const deleteArtifact = useCallback(
    async (storageKey: string): Promise<boolean> => {
      if (!authenticated) return false;
      try {
        await remove(storageKey);
        return true;
      } catch (error) {
        console.error('[artifact-store] delete failed:', storageKey, error);
        return false;
      }
    },
    [remove, authenticated],
  );

  const persistArtifact = useCallback(
    async (input: PersistArtifactInput): Promise<string | null> => {
      if (!authenticated) return null;
      return persistArtifactWith(upload, input);
    },
    [upload, authenticated],
  );

  const persistAttachments = useCallback(
    (
      files: UploadedFile[],
      attachments: MessageAttachment[],
      opts: { sessionId: string; isPrivate: boolean; messageId?: string },
    ): Promise<MessageAttachment[]> =>
      persistAttachmentsWith(upload, files, attachments, { ...opts, authenticated }),
    [upload, authenticated],
  );

  /**
   * How many artifacts a conversation has, and how big they are.
   *
   * Powers the delete confirmation. The user is told what they are about to
   * destroy BEFORE they destroy it — a bare "also delete artifacts?" with no
   * number is a question nobody can answer responsibly.
   *
   * Returns null when the total cannot be established, which the dialog must
   * treat as "unknown" rather than "none": showing "0 files" for a listing
   * failure would invite the user to delete a conversation believing it had no
   * artifacts, and take real ones with it.
   */
  const summariseArtifacts = useCallback(
    async (sessionId: string): Promise<ArtifactSummary | null> => {
      if (!authenticated) return null;

      const prefix = sessionPrefix(sessionId);
      if (!isSafeSessionPrefix(prefix)) return null;

      try {
        // `listPrefix` follows the cursor: a single page would under-count, and
        // an under-count in a delete confirmation misleads in the worst direction.
        const items = await listPrefix(prefix);
        return {
          count: items.length,
          bytes: items.reduce((sum, i) => sum + (i.size ?? 0), 0),
        };
      } catch (error) {
        console.error('[artifact-store] could not summarise artifacts:', sessionId, error);
        return null;
      }
    },
    [authenticated, listPrefix],
  );

  /**
   * Delete every artifact a conversation produced.
   *
   * NON-ATOMIC by necessity: neither storage backend implements a bulk delete,
   * so this is a list-and-delete loop that can partially fail. The caller must
   * report what actually happened rather than assuming success — a user told
   * "deleted" while three files remain is worse than one told the truth.
   */
  const deleteSessionArtifacts = useCallback(
    async (sessionId: string): Promise<PrefixDeleteResult> => {
      if (!authenticated) return { deleted: 0, failed: 0 };

      const prefix = sessionPrefix(sessionId);
      // Never let a malformed session id widen the blast radius.
      if (!isSafeSessionPrefix(prefix)) {
        console.error('[artifact-store] refusing unsafe delete prefix:', prefix);
        return { deleted: 0, failed: 0 };
      }

      try {
        const result = await removePrefix(prefix);
        return { deleted: result.deleted, failed: result.failed.length };
      } catch (error) {
        console.error('[artifact-store] prefix delete failed:', prefix, error);
        // Unknown rather than zero — the caller reports it as a failure.
        return { deleted: 0, failed: -1 };
      }
    },
    [authenticated, removePrefix],
  );

  /**
   * Assign keys for a message's attachments without uploading.
   *
   * Exposed so the send path can put keys on the wire before the bytes exist —
   * see `assignArtifactKeys`.
   */
  const assignKeys = useCallback(
    (
      attachments: MessageAttachment[],
      opts: { sessionId: string; isPrivate: boolean },
    ): MessageAttachment[] =>
      assignArtifactKeys(attachments, { ...opts, authenticated }),
    [authenticated],
  );

  return {
    persistArtifact,
    persistAttachments,
    deleteArtifact,
    assignKeys,
    summariseArtifacts,
    deleteSessionArtifacts,
  };
}

/**
 * Decode a data URL to a Blob.
 *
 * Returns null rather than throwing: a malformed data URL should degrade to
 * "not persisted", which the caller already handles, not abort the send.
 */
export async function dataUrlToBlob(dataUrl: string): Promise<Blob | null> {
  if (!dataUrl.startsWith('data:')) return null;
  try {
    const res = await fetch(dataUrl);
    return await res.blob();
  } catch {
    return null;
  }
}
